import * as React from 'react';

import { LiveViewRow } from '../live_view/interfaces';
import { DEFAULT_SORT, NOT_IN_ACTIVITY, REDACTED_ACTIVITY_NAME, StudentNameWithStatus, TimeSpent, gated, showcaseUid, sortRows } from '../live_view/liveViewTable';
import PromptCircles from '../live_view/promptCircles';
import { useLiveViewRows } from '../live_view/useLiveViewRows';
import { DataTable, safeParseHtml } from '../../../Shared/index';

const MAX_ROWS = 5;
const LIVE_VIEW_PATH = '/teachers/progress_reports/live_view';

const headers = [
  { width: '170px', name: 'Student', attribute: 'studentName', noTooltip: true },
  { width: '90px', name: 'Time spent', attribute: 'timeSpent', noTooltip: true },
  { width: '250px', name: 'Activity', attribute: 'activityName' },
  { width: '180px', name: 'Prompts', attribute: 'promptsCompleted', noTooltip: true, headerClassName: 'last-section', rowSectionClassName: 'last-section' },
];

const studentName = (row: LiveViewRow) => <StudentNameWithStatus row={row} />;

// The dashboard widget: the same rows as the report, laid out like the
// activity feed beside it, in-progress students first
export const widgetRows = (students: LiveViewRow[], fullAccess: boolean, now: Date) => {
  const showcase = fullAccess ? null : showcaseUid(students);

  // Five rows only, so students who may need help come first, then by time spent
  const ordered = sortRows(students, DEFAULT_SORT, now);
  const needHelp = (row: LiveViewRow) => row.lowScore || row.tooLong;
  const prioritized = [...ordered.filter(needHelp), ...ordered.filter(row => !needHelp(row))];

  return prioritized.slice(0, MAX_ROWS).map(row => {
    if (row.onlineOnly) {
      return { id: row.activitySessionUid, studentName: studentName(row), timeSpent: '', activityName: NOT_IN_ACTIVITY, promptsCompleted: '' };
    }
    const blur = !(!row.redacted && (fullAccess || row.activitySessionUid === showcase || row.completed));
    return {
      id: row.activitySessionUid,
      studentName: studentName(row),
      timeSpent: <TimeSpent now={now} row={row} />,
      activityName: gated(safeParseHtml(row.activityName || REDACTED_ACTIVITY_NAME), blur),
      promptsCompleted: gated(<PromptCircles completed={row.completed} pending={row.progressPending} prompts={row.prompts} stage={row.stage} totalPrompts={row.totalPrompts} />, blur),
    };
  });
};

interface LiveViewMiniProps {
  // School or District Premium: every student in full
  fullAccess: boolean,
}

const LiveViewMini = ({ fullAccess, }: LiveViewMiniProps) => {
  const { students, loading, now } = useLiveViewRows({ details: true });

  if (loading || !students.length) { return <span />; }

  return (
    <section aria-labelledby="liveViewTitle" className="activity-feed populated live-view-mini">
      <header>
        <h2 id="liveViewTitle">Live View</h2>
        <a className="quill-button outlined extra-small grey focus-on-light" href={LIVE_VIEW_PATH}>View all</a>
      </header>
      <DataTable headers={headers} rows={widgetRows(students, fullAccess, now)} />
    </section>
  );
};

export default LiveViewMini;
